import { useDispatch, useSelector } from "react-redux";
import { createCategory, setBaseFields, updateCategory } from "../../features/categorySlice";
import { errorToastify, successToastify } from "../../utils/toastify.util";
import { useEffect, useRef, useState } from "react";
import { Formik, Form, Field } from "formik";
import { basicModalShowHide } from "../../features/modalSlice";
import instance from "../../utils/api_instance.util";
import IapImageCropperModal from "../../components/image/IapImageCropperModal.jsx";
import { categorySchema } from "../../utils/validation.util";
import { InputError, LevelReq } from "../../components/form/FormBody";

const CategoryModal = ({ closeModal }) => {
    const dispatch = useDispatch();
    const { isBtnLoad, isRedirect, fields } = useSelector((state) => state.category);
    const [uploadingField, setUploadingField] = useState("");
    const [cropState, setCropState] = useState({
        isOpen: false,
        imageSrc: "",
        fileName: "category-image.jpg",
        fieldName: "",
    });
    const fileInputRefs = useRef({});
    const setFieldValueRef = useRef(null);

    useEffect(() => () => dispatch(setBaseFields()), [dispatch]);

    useEffect(() => {
        if (isRedirect) {
            dispatch(basicModalShowHide());
        }
    }, [isRedirect, dispatch]);

    const buildCroppedFileName = (originalName, prefix) => {
        const safePrefix = prefix || "cropped-image";
        const sourceName = String(originalName || "").trim();
        const baseName = sourceName.replace(/\.[^/.]+$/, "") || safePrefix;
        const normalizedBase = baseName
            .replace(/[^a-zA-Z0-9-_]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .toLowerCase();

        return `${normalizedBase || safePrefix}-${Date.now()}.jpg`;
    };

    const uploadFile = async (file) => {
        const fileData = new FormData();
        fileData.append("file", file);

        const { data } = await instance.post("/api/v1/common/file/upload", fileData);

        if (!data.success) {
            throw new Error(data?.message || "Upload failed");
        }

        return data?.result?.url || "";
    };

    const closeCropper = () => {
        if (cropState.imageSrc) {
            URL.revokeObjectURL(cropState.imageSrc);
        }
        setCropState({
            isOpen: false,
            imageSrc: "",
            fileName: "category-image.jpg",
            fieldName: "",
        });
    };

    const handleFileUpload = async (event, fieldName) => {
        const file = event.currentTarget.files?.[0];
        if (!file) return;

        try {
            setCropState({
                isOpen: true,
                imageSrc: URL.createObjectURL(file),
                fileName: file.name || `${fieldName}.jpg`,
                fieldName,
            });
        } finally {
            event.target.value = "";
        }
    };

    const uploadCroppedImage = async (blob) => {
        const { fieldName, fileName } = cropState;
        if (!fieldName) return;

        try {
            setUploadingField(fieldName);
            const uploadFileBlob = new File(
                [blob],
                buildCroppedFileName(fileName, fieldName),
                { type: "image/jpeg" },
            );
            const uploadedUrl = await uploadFile(uploadFileBlob);
            setFieldValueRef.current?.(fieldName, uploadedUrl);
            successToastify("Uploaded");
            closeCropper();
        } catch (err) {
            errorToastify(err?.response?.data?.message || `${fieldName} upload failed`);
        } finally {
            setUploadingField("");
        }
    };

    const triggerFileInput = (fieldName) => {
        fileInputRefs.current[fieldName]?.click();
    };

    const imageFields = [
        {
            key: "logo",
            label: "Icon",
            ratioText: "1:1",
            targetWidth: 1,
            targetHeight: 1,
            previewWidth: "120px",
            previewAspectRatio: "1 / 1",
            colClass: "col-md-6",
        },
        {
            key: "banner",
            label: "Button Image",
            ratioText: "1:1",
            targetWidth: 1,
            targetHeight: 1,
            previewWidth: "120px",
            previewAspectRatio: "1 / 1",
            colClass: "col-md-6",
        },
        {
            key: "coverimage",
            label: "Hero Image",
            ratioText: "16:9",
            targetWidth: 16,
            targetHeight: 9,
            previewWidth: "220px",
            previewAspectRatio: "16 / 9",
            colClass: "col-md-12",
        },
    ];

    const activeImageField = imageFields.find((field) => field.key === cropState.fieldName);

    const getUploadMessage = (fieldName) => {
        const field = imageFields.find((item) => item.key === fieldName);
        return field ? `Uploading ${field.label.toLowerCase()}...` : "Uploading...";
    };

    const handleSubmitCategory = (values) => {
        const name = values.name.trim();
        const description = values.description.trim();
        const updatedFormData = { ...values, name, description };

        if (updatedFormData.id === "") {
            dispatch(createCategory(updatedFormData));
        } else {
            dispatch(updateCategory(updatedFormData));
        }
    };

    return (
        <div className="container-fluid px-1">
            <Formik
                initialValues={fields}
                enableReinitialize
                validationSchema={categorySchema}
                onSubmit={handleSubmitCategory}
            >
                {({ values, setFieldValue }) => {
                    setFieldValueRef.current = setFieldValue;

                    return (
                        <Form>
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <LevelReq htmlFor="name" className="form-label">
                                        Name
                                    </LevelReq>
                                    <Field type="text" name="name" id="name" className="form-control" />
                                    <InputError name="name" />
                                </div>
                                <div className="col-md-12">
                                    <LevelReq htmlFor="description" className="form-label">
                                        Description
                                    </LevelReq>
                                    <Field
                                        as="textarea"
                                        name="description"
                                        id="description"
                                        className="form-control"
                                        style={{ resize: "none", height: "100px" }}
                                    />
                                    <InputError name="description" />
                                </div>
                                <div className="col-md-12">
                                    <div
                                        style={{
                                            border: "1px solid rgba(15, 23, 42, 0.08)",
                                            borderRadius: "14px",
                                            padding: "16px",
                                            backgroundColor: "#fbfcfe",
                                        }}
                                    >
                                        <div className="mb-3">
                                            <div className="fw-semibold" style={{ color: "#1f2937" }}>Images</div>
                                            <div className="text-muted small">
                                                Upload and crop each image before saving the collection.
                                            </div>
                                        </div>
                                        <div className="row g-3">
                                            {imageFields.map((imageField) => (
                                                <div className={imageField.colClass} key={imageField.key}>
                                                    <div
                                                        style={{
                                                            border: "1px solid rgba(15, 23, 42, 0.1)",
                                                            borderRadius: "12px",
                                                            padding: "14px",
                                                            backgroundColor: "#fff",
                                                            height: "100%",
                                                        }}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <LevelReq className="form-label mb-1">
                                                                    {imageField.label}
                                                                </LevelReq>
                                                                <div className="text-muted small">
                                                                    Aspect ratio: {imageField.ratioText}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <input
                                                            ref={(node) => {
                                                                fileInputRefs.current[imageField.key] = node;
                                                            }}
                                                            type="file"
                                                            accept="image/*"
                                                            style={{ display: "none" }}
                                                            onChange={(e) => handleFileUpload(e, imageField.key)}
                                                            disabled={isBtnLoad || uploadingField === imageField.key}
                                                        />
                                                        <div className="d-flex align-items-center gap-3 flex-wrap">
                                                            <div
                                                                style={{
                                                                    width: imageField.previewWidth,
                                                                    aspectRatio: imageField.previewAspectRatio,
                                                                    borderRadius: "10px",
                                                                    overflow: "hidden",
                                                                    border: "1px solid rgba(15, 23, 42, 0.12)",
                                                                    backgroundColor: "#f8fafc",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {values[imageField.key] ? (
                                                                    <img
                                                                        src={values[imageField.key]}
                                                                        alt={imageField.label}
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover",
                                                                            display: "block",
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span className="text-muted small">No image</span>
                                                                )}
                                                            </div>
                                                            <div className="d-flex flex-column align-items-start gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => triggerFileInput(imageField.key)}
                                                                    disabled={isBtnLoad || uploadingField === imageField.key}
                                                                >
                                                                    {values[imageField.key]
                                                                        ? `Change ${imageField.label}`
                                                                        : `Upload ${imageField.label}`}
                                                                </button>
                                                                {values[imageField.key] ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() =>
                                                                            setFieldValue(imageField.key, "")
                                                                        }
                                                                        disabled={isBtnLoad}
                                                                    >
                                                                        Remove {imageField.label}
                                                                    </button>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        {uploadingField === imageField.key && (
                                                            <small className="text-muted d-block mt-2">
                                                                {getUploadMessage(imageField.key)}
                                                            </small>
                                                        )}
                                                        <InputError name={imageField.key} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <IapImageCropperModal
                                    isOpen={cropState.isOpen}
                                    imageSrc={cropState.imageSrc}
                                    onCancel={closeCropper}
                                    onConfirm={uploadCroppedImage}
                                    title={activeImageField ? `Crop ${activeImageField.label}` : "Crop Image"}
                                    targetWidth={activeImageField?.targetWidth || 1}
                                    targetHeight={activeImageField?.targetHeight || 1}
                                    useSourceDimensions
                                />
                                <div className="col-md-12">
                                    <div className="d-flex justify-content-end gap-2 pt-2">
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            disabled={isBtnLoad}
                                            onClick={closeModal}
                                        >
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={isBtnLoad}>
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default CategoryModal;
